import { useEffect, useRef, useState, type ChangeEvent, type DragEvent, type RefObject } from 'react';
import { validateAttachmentFile, type PendingAtt } from './attachments';

export interface ChatAttachmentsApi {
  pending: PendingAtt[];
  dropHover: boolean;
  imageInputRef: RefObject<HTMLInputElement | null>;
  docInputRef: RefObject<HTMLInputElement | null>;
  pickFiles: (e: ChangeEvent<HTMLInputElement>) => void;
  removePending: (id: string) => void;
  /** Revoke all preview blobs and empty the queue (called after a send). */
  clearPending: () => void;
  /** Spread onto the chat container to enable drag-and-drop. */
  dragProps: {
    onDragEnter: (e: DragEvent) => void;
    onDragOver: (e: DragEvent) => void;
    onDragLeave: (e: DragEvent) => void;
    onDrop: (e: DragEvent) => void;
  };
}

/**
 * Pending image/document attachments for the chat composer: the queue, file
 * pickers, drag-and-drop and blob-URL lifecycle. Extracted verbatim from
 * Chat.tsx (NAI-220). `disabled` blocks drops mid-stream; `onError` surfaces
 * validation failures.
 */
export function useChatAttachments({
  disabled,
  onError,
}: {
  disabled: boolean;
  onError: (msg: string) => void;
}): ChatAttachmentsApi {
  const [pending, setPending] = useState<PendingAtt[]>([]);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);
  const [dropHover, setDropHover] = useState(false);
  const dragDepth = useRef(0);

  /* Revoke blob URLs once the message is sent so memory is freed. */
  useEffect(() => {
    return () => {
      for (const p of pending) URL.revokeObjectURL(p.previewUrl);
    };
  }, [pending]);

  /* Shared by the two pickers (images / documents) and drag-and-drop. */
  const addFiles = (files: Iterable<File>) => {
    const next: PendingAtt[] = [];
    for (const file of files) {
      const err = validateAttachmentFile(file);
      if (err) {
        onError(err);
        continue;
      }
      next.push({
        id: `${Date.now()}-${file.name}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        previewUrl: URL.createObjectURL(file),
      });
    }
    if (next.length) setPending((p) => [...p, ...next]);
  };

  const pickFiles = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFiles(e.target.files);
    /* Reset so picking the same file twice still fires onChange. */
    e.target.value = '';
  };

  /* Drag-and-drop anywhere on the chat. Counter avoids the enter/leave flicker */
  /* when dragging across child elements. */
  const onDragEnter = (e: DragEvent) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDropHover(true);
  };
  const onDragOver = (e: DragEvent) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
  };
  const onDragLeave = (e: DragEvent) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDropHover(false);
  };
  const onDrop = (e: DragEvent) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    dragDepth.current = 0;
    setDropHover(false);
    if (disabled) return;
    addFiles(e.dataTransfer.files);
  };

  const removePending = (id: string) => {
    setPending((p) => {
      const found = p.find((x) => x.id === id);
      if (found) URL.revokeObjectURL(found.previewUrl);
      return p.filter((x) => x.id !== id);
    });
  };

  const clearPending = () => {
    setPending((p) => {
      for (const x of p) URL.revokeObjectURL(x.previewUrl);
      return [];
    });
  };

  return {
    pending,
    dropHover,
    imageInputRef,
    docInputRef,
    pickFiles,
    removePending,
    clearPending,
    dragProps: { onDragEnter, onDragOver, onDragLeave, onDrop },
  };
}
